// src/pages/DuelPage.js — v3 · Système de combat Skills
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  collection, doc, addDoc, updateDoc, deleteDoc,
  onSnapshot, query, where, orderBy, serverTimestamp, getDoc
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";
import { ONI_FORMS } from "../data/demons";
import { ALL_SKILLS, TYPE_META, getOniSkills, resolveBattle } from "../data/battleSkills";
import EmptyState from "../components/EmptyState";
import { useClaude, ClaudeError } from "../hooks/useClaude";
import "./DuelPage.css";

const CLAUDE_URL = `${process.env.REACT_APP_API_URL || "http://localhost:3001"}/claude`;
const TIMEOUT_SECONDS = 120; // 2 minutes pour répondre

// ── Helpers ───────────────────────────────────────────────────────────────────
function enrichDemon(demon) {
  if (!demon?.oni?.id) return demon;
  const fresh = ONI_FORMS.find(o => o.id === demon.oni.id);
  if (!fresh) return demon;
  return { ...demon, oni: { ...fresh, ...demon.oni, image: fresh.image, emoji: fresh.emoji } };
}

function OniAvatar({ oni, size = 60 }) {
  if (oni?.image) return (
    <div style={{ width:size, height:size, overflow:"hidden", border:"2px solid var(--border-red)", flexShrink:0 }}>
      <img src={`${oni.image}?v=2`} alt={oni.name} style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"center top" }}/>
    </div>
  );
  return <span style={{ fontSize:size*0.6 }}>{oni?.emoji || "👹"}</span>;
}

function buildNarrationPrompt(p1, p2, rounds, winner) {
  const roundTexts = rounds.map((r, i) =>
    `Round ${i+1}: ${p1.name} joue "${r.p1Skill.name}" (${r.p1Skill.type}) vs ${p2.name} joue "${r.p2Skill.name}" (${r.p2Skill.type}) → ${r.winner === "p1" ? p1.name : r.winner === "p2" ? p2.name : "égalité"} gagne l'échange (${r.dmg1} vs ${r.dmg2} dégâts)`
  ).join("\n");

  return `Tu es le Chroniqueur des Duels Takkyu Oni. Narre ce set de ping en style épique et concis.

DUEL: ${p1.oni.emoji} ${p1.name} (${p1.oni.name}) VS ${p2.oni.emoji} ${p2.name} (${p2.oni.name})

RÉSULTATS:
${roundTexts}

HP FINAL: ${p1.name}: ${winner.p1HP}/100 · ${p2.name}: ${winner.p2HP}/100
VAINQUEUR: ${winner.winner === "p1" ? p1.name : winner.winner === "p2" ? p2.name : "ÉGALITÉ"}

Génère en JSON:
{
  "round1": "narration courte du round 1 (1-2 phrases, dans le lore)",
  "round2": "narration courte du round 2",
  "round3": "narration courte du round 3 — le point décisif",
  "verdict": "phrase finale épique sur le vainqueur (ou l'égalité)",
  "winner_cry": "cri de victoire du gagnant en 5 mots max",
  "score": "${winner.p1HP}-${winner.p2HP}"
}`;
}

// ── Skill card ────────────────────────────────────────────────────────────────
function SkillCard({ skill, selected, onClick, disabled }) {
  const meta = TYPE_META[skill.type];
  return (
    <button
      className={`skill-card${selected ? " selected" : ""}${disabled ? " disabled" : ""}`}
      style={{ "--sc": meta.color }}
      onClick={onClick}
      disabled={disabled}
    >
      <div className="skill-card-header">
        <span className="skill-icon">{skill.icon}</span>
        <span className="skill-type-badge" style={{ color: meta.color }}>{meta.icon} {meta.label}</span>
      </div>
      <p className="skill-name">{skill.name}</p>
      <p className="skill-jp">{skill.jp}</p>
      <p className="skill-desc">{skill.desc}</p>
      {skill.bonus && <p className="skill-bonus">⚡ {skill.bonus}</p>}
      <div className="skill-damage-row">
        <span className="skill-dmg">{skill.damage} DMG</span>
        <span className="skill-stat">+{skill.statBoost}</span>
      </div>
    </button>
  );
}

// ── Combat en cours (attente + sélection skills) ──────────────────────────────
function ActiveDuelView({ duel, myUid, profile }) {
  const myDemon    = enrichDemon(profile?.demon);
  const myOni      = myDemon?.oni;
  const mySkills   = getOniSkills(myOni, myDemon?.stats, myDemon?.perks);
  const isChallenger = duel.challengerUid === myUid;
  const myRole     = isChallenger ? "challenger" : "opponent";
  const myChoices  = duel[`${myRole}Skills`] || [];
  const hasChosen  = myChoices.length === 3;
  const otherHasChosen = isChallenger ? (duel.opponentSkills?.length === 3) : (duel.challengerSkills?.length === 3);
  const [selected, setSelected] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const toggle = (id) => {
    if (selected.includes(id)) setSelected(s => s.filter(x => x !== id));
    else if (selected.length < 3) setSelected(s => [...s, id]);
  };

  const submitSkills = async () => {
    if (selected.length !== 3 || submitting) return;
    setSubmitting(true);
    await updateDoc(doc(db, "duels", duel.id), {
      [`${myRole}Skills`]: selected,
      [`${myRole}SubmittedAt`]: serverTimestamp(),
    });
    setSubmitting(false);
  };

  const cancelDuel = async () => {
    if (!window.confirm("Annuler ce duel ?")) return;
    await deleteDoc(doc(db, "duels", duel.id));
  };

  // Timeout check
  const createdAt = duel.createdAt?.toDate?.() || new Date();
  const elapsed   = Math.floor((Date.now() - createdAt.getTime()) / 1000);
  const remaining = Math.max(0, TIMEOUT_SECONDS - elapsed);
  const timedOut  = remaining === 0 && !otherHasChosen;

  return (
    <div className="active-duel">
      {/* Combattants */}
      <div className="duel-matchup">
        <div className="duel-fighter">
          <OniAvatar oni={myOni} size={70}/>
          <p className="fighter-name">{profile?.displayName}</p>
          <p className="fighter-oni">{myOni?.name}</p>
          <span className={`fighter-status ${hasChosen ? "ready" : "waiting"}`}>
            {hasChosen ? "✓ PRÊT" : "EN ATTENTE"}
          </span>
        </div>
        <div className="duel-vs">VS</div>
        <div className="duel-fighter">
          <OniAvatar oni={enrichDemon(isChallenger ? duel.opponentDemon : duel.challengerDemon)?.oni} size={70}/>
          <p className="fighter-name">{isChallenger ? duel.opponentName : duel.challengerName}</p>
          <p className="fighter-oni">{(isChallenger ? duel.opponentDemon : duel.challengerDemon)?.oni?.name}</p>
          <span className={`fighter-status ${otherHasChosen ? "ready" : "waiting"}`}>
            {otherHasChosen ? "✓ PRÊT" : `⏳ ${remaining}s`}
          </span>
        </div>
      </div>

      {timedOut && (
        <div className="duel-timeout">
          L'adversaire n'a pas répondu à temps.
          <button className="btn btn-ghost btn-sm" onClick={cancelDuel}>ANNULER LE DUEL</button>
        </div>
      )}

      {!hasChosen && !timedOut && (
        <div className="skill-selection">
          <p className="skill-selection-title">CHOISIS 3 SKILLS · {selected.length}/3</p>
          <p className="skill-selection-sub">L'ordre compte — c'est l'ordre de jeu des 3 rounds.</p>
          {selected.length > 0 && (
            <div className="selected-order">
              {selected.map((id, i) => {
                const s = ALL_SKILLS.find(x => x.id === id);
                return s ? (
                  <div key={id} className="selected-slot">
                    <span className="slot-num">{i+1}</span>
                    <span>{s.icon}</span>
                    <span className="slot-name">{s.name}</span>
                    <button onClick={() => toggle(id)} className="slot-remove">✕</button>
                  </div>
                ) : null;
              })}
            </div>
          )}
          <div className="skills-grid">
            {mySkills.map(skill => (
              <SkillCard key={skill.id} skill={skill}
                selected={selected.includes(skill.id)}
                disabled={selected.length >= 3 && !selected.includes(skill.id)}
                onClick={() => toggle(skill.id)}
              />
            ))}
          </div>
          <button className="btn btn-primary duel-submit-btn"
            onClick={submitSkills}
            disabled={selected.length !== 3 || submitting}>
            {submitting ? <><span className="spinner"/>ENVOI...</> : "⚔️ ENVOYER MES SKILLS →"}
          </button>
        </div>
      )}

      {hasChosen && !otherHasChosen && (
        <div className="duel-waiting">
          <span className="spinner" style={{width:24,height:24,borderWidth:3}}/>
          <p>Skills envoyés. En attente de {isChallenger ? duel.opponentName : duel.challengerName}...</p>
          <button className="btn btn-ghost btn-sm" onClick={cancelDuel} style={{marginTop:"1rem"}}>ANNULER</button>
        </div>
      )}
    </div>
  );
}

// ── Résultat du duel ──────────────────────────────────────────────────────────
function DuelResultView({ duel, myUid }) {
  const result    = duel.result;
  const rounds    = result?.rounds || [];
  const narration = duel.narration;
  const isChallenger = duel.challengerUid === myUid;
  const iWon = result?.winner === (isChallenger ? "p1" : "p2");
  const p1Name = duel.challengerName;
  const p2Name = duel.opponentName;

  return (
    <div className="duel-result">
      <div className={`duel-result-banner ${iWon ? "won" : result?.winner === "draw" ? "draw" : "lost"}`}>
        <span className="result-title">
          {result?.winner === "draw" ? "⚡ ÉGALITÉ" : iWon ? "🏆 VICTOIRE" : "💀 DÉFAITE"}
        </span>
        {narration?.winner_cry && <p className="result-cry">"{narration.winner_cry}"</p>}
      </div>

      {/* HP bars */}
      <div className="duel-hp-summary">
        <div className="hp-row">
          <span>{p1Name}</span>
          <div className="hp-bar-bg">
            <div className="hp-bar-fill" style={{ width: `${result?.p1HP || 0}%`, background: isChallenger ? "var(--blood)" : "var(--ash)" }}/>
          </div>
          <span className="hp-val">{result?.p1HP || 0}</span>
        </div>
        <div className="hp-row">
          <span>{p2Name}</span>
          <div className="hp-bar-bg">
            <div className="hp-bar-fill" style={{ width: `${result?.p2HP || 0}%`, background: !isChallenger ? "var(--blood)" : "var(--ash)" }}/>
          </div>
          <span className="hp-val">{result?.p2HP || 0}</span>
        </div>
      </div>

      {/* Rounds */}
      <div className="duel-rounds">
        {rounds.map((r, i) => (
          <div key={i} className="duel-round">
            <p className="round-label">ROUND {i+1}</p>
            <div className="round-skills">
              <div className={`round-skill ${r.winner === "p1" ? "winner" : ""}`}>
                <span>{r.p1Skill.icon}</span>
                <span className="round-skill-name">{r.p1Skill.name}</span>
                <span className="round-dmg">-{r.dmg1}</span>
              </div>
              <span className="round-vs">⚔</span>
              <div className={`round-skill ${r.winner === "p2" ? "winner" : ""}`}>
                <span>{r.p2Skill.icon}</span>
                <span className="round-skill-name">{r.p2Skill.name}</span>
                <span className="round-dmg">-{r.dmg2}</span>
              </div>
            </div>
            {narration && (
              <p className="round-narration">
                {narration[`round${i+1}`]}
              </p>
            )}
          </div>
        ))}
      </div>

      {narration?.verdict && (
        <div className="duel-verdict">
          <p>"{narration.verdict}"</p>
        </div>
      )}
    </div>
  );
}

// ── Main DuelPage ─────────────────────────────────────────────────────────────
export default function DuelPage() {
  const { user, profile } = useAuth();
  const { generate, loading: aiLoading } = useClaude();
  const [myDuels, setMyDuels] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [search, setSearch]   = useState("");
  const [view, setView]       = useState("list"); // list | search | active | result
  const [activeDuel, setActiveDuel] = useState(null);
  const [challenging, setChallenging] = useState(false);
  const [processing, setProcessing]   = useState(false);
  const [narrationError, setNarrationError] = useState(null);

  const myDemon = enrichDemon(profile?.demon);
  const myOni   = myDemon?.oni;

  // Listen to my duels
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, "duels"),
      where("participants", "array-contains", user.uid),
      orderBy("createdAt", "desc")
    );
    return onSnapshot(q, snap => {
      const duels = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setMyDuels(duels);
      // Auto-process if both sides submitted
      const pending = duels.find(d =>
        d.status === "waiting_resolution" &&
        d.challengerSkills?.length === 3 &&
        d.opponentSkills?.length === 3
      );
      if (pending && !processing) processResolution(pending);
    });
  }, [user, processing]);

  // Process resolution when both players have chosen
  const processResolution = async (duel) => {
    if (processing) return;
    setProcessing(true);
    try {
      const p1Stats = duel.challengerDemon?.stats;
      const p2Stats = duel.opponentDemon?.stats;
      const result  = resolveBattle(duel.challengerSkills, duel.opponentSkills, p1Stats, p2Stats);

      const p1 = { name: duel.challengerName, oni: duel.challengerDemon?.oni || {} };
      const p2 = { name: duel.opponentName,   oni: duel.opponentDemon?.oni || {} };

      // Narration IA
      let narration = null;
      try {
        const controller = new AbortController();
        const t = setTimeout(() => controller.abort(), 20000);
        const res = await fetch(CLAUDE_URL, {
          method: "POST", signal: controller.signal,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            model: "claude-sonnet-4-5", max_tokens: 400,
            messages: [{ role:"user", content: buildNarrationPrompt(p1, p2, result.rounds, result) }]
          })
        });
        clearTimeout(t);
        const data = await res.json();
        const text = data.content?.find(b => b.type==="text")?.text || "";
        narration = JSON.parse(text.replace(/```json|```/g,"").trim());
      } catch(e) {
        narration = {
          round1: "Le premier échange s'ouvre dans le silence.",
          round2: "Les forces s'affrontent sans merci.",
          round3: "Le point décisif est joué.",
          verdict: result.winner === "draw" ? "Égalité rare entre deux démons." : `${result.winner === "p1" ? p1.name : p2.name} remporte ce duel.`,
          winner_cry: result.winner === "draw" ? "Honneur égal" : "Le démon triomphe",
        };
      }

      // Save result
      const winner = result.winner === "p1" ? duel.challengerUid : result.winner === "p2" ? duel.opponentUid : null;
      await updateDoc(doc(db, "duels", duel.id), {
        status: "completed",
        result: { rounds: result.rounds.map(r => ({
          p1SkillId: r.p1Skill.id, p1SkillName: r.p1Skill.name, p1SkillIcon: r.p1Skill.icon,
          p2SkillId: r.p2Skill.id, p2SkillName: r.p2Skill.name, p2SkillIcon: r.p2Skill.icon,
          winner: r.winner, dmg1: r.dmg1, dmg2: r.dmg2,
          p1HPAfter: r.p1HPAfter, p2HPAfter: r.p2HPAfter,
        })), p1HP: result.p1HP, p2HP: result.p2HP, winner: result.winner },
        narration,
        winnerUid: winner,
        resolvedAt: serverTimestamp(),
      });

      // XP
      if (winner) {
        const loserUid = winner === duel.challengerUid ? duel.opponentUid : duel.challengerUid;
        const wSnap = await getDoc(doc(db, "users", winner));
        if (wSnap.exists()) {
          await updateDoc(doc(db, "users", winner), { xp: (wSnap.data().xp||0) + 50, weeklyDuelsWon: (wSnap.data().weeklyDuelsWon||0) + 1 });
        }
        const lSnap = await getDoc(doc(db, "users", loserUid));
        if (lSnap.exists()) await updateDoc(doc(db, "users", loserUid), { xp: (lSnap.data().xp||0) + 10 });
      }
    } catch(e) { console.error("[Duel resolve]", e); }
    setProcessing(false);
  };

  // Challenge a user
  const challengeUser = async (target) => {
    if (challenging || !user || !myDemon) return;
    setChallenging(true);
    try {
      const ref = await addDoc(collection(db, "duels"), {
        status: "waiting_skills",
        participants: [user.uid, target.uid],
        challengerUid: user.uid,
        challengerName: profile?.displayName || "Oni",
        challengerDemon: { oni: myOni, stats: myDemon.stats, perks: myDemon.perks },
        opponentUid: target.uid,
        opponentName: target.displayName || "Oni",
        opponentDemon: { oni: enrichDemon(target.demon)?.oni, stats: target.demon?.stats, perks: target.demon?.perks },
        challengerSkills: [],
        opponentSkills: [],
        createdAt: serverTimestamp(),
      });
      // Notification
      await addDoc(collection(db, "users", target.uid, "notifications"), {
        type: "DUEL_CHALLENGE", title: `${profile?.displayName} te défie en duel !`,
        body: `Choisis tes 3 skills — 2 minutes pour répondre.`,
        link: "/duel", fromName: profile?.displayName, fromEmoji: myOni?.emoji,
        read: false, createdAt: serverTimestamp(),
      });
      setView("list");
    } catch(e) { console.error(e); }
    setChallenging(false);
  };

  const searchUsers = async () => {
    if (search.trim().length < 2) return;
    const snap = await getDocs?.(query(collection(db, "users"), limit(20)));
    // Fallback simple
  };

  if (!user || !profile) return (
    <div className="duel-page section" style={{paddingTop:"8rem"}}>
      <EmptyState type="duel_no_demon"/>
    </div>
  );

  if (!myDemon) return (
    <div className="duel-page section" style={{paddingTop:"8rem"}}>
      <EmptyState type="duel_no_demon"/>
    </div>
  );

  const activeDuels    = myDuels.filter(d => d.status !== "completed");
  const completedDuels = myDuels.filter(d => d.status === "completed");

  return (
    <div className="duel-page">
      <div className="section" style={{paddingTop:"7rem"}}>
        <div className="duel-header">
          <div>
            <span className="section-eyebrow">決闘 · Duels</span>
            <h1 className="section-title">Combat<br/>Démoniaque</h1>
            <div className="divider"/>
          </div>
          <div className="duel-my-oni">
            <OniAvatar oni={myOni} size={60}/>
            <div>
              <p className="duel-my-name">{profile.displayName}</p>
              <p className="duel-my-oni-name">{myOni?.name}</p>
            </div>
          </div>
        </div>

        {/* Mon set de skills */}
        <div className="my-skills-preview">
          <p className="skills-preview-title">MON SET · {getOniSkills(myOni, myDemon?.stats, myDemon?.perks).length} SKILLS DISPONIBLES</p>
          <div className="skills-preview-row">
            {getOniSkills(myOni, myDemon?.stats, myDemon?.perks).map(s => {
              const meta = TYPE_META[s.type];
              return (
                <div key={s.id} className="skill-chip" style={{"--sc": meta.color}}>
                  <span>{s.icon}</span>
                  <span>{s.name}</span>
                  <span className="skill-chip-type">{meta.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Duels actifs */}
        {activeDuels.length > 0 && (
          <div className="duel-section">
            <p className="duel-section-title">DUELS EN COURS · {activeDuels.length}</p>
            {activeDuels.map(duel => (
              <div key={duel.id} className="duel-card active-duel-card"
                onClick={() => setActiveDuel(duel)}>
                <div className="duel-card-header">
                  <OniAvatar
                    oni={enrichDemon(duel.challengerUid === user.uid ? duel.opponentDemon : duel.challengerDemon)?.oni}
                    size={44}/>
                  <div className="duel-card-info">
                    <p className="duel-card-name">
                      vs {duel.challengerUid === user.uid ? duel.opponentName : duel.challengerName}
                    </p>
                    <p className="duel-card-status">
                      {duel[`${duel.challengerUid === user.uid ? "challenger" : "opponent"}Skills`]?.length === 3
                        ? "✓ Skills envoyés — en attente"
                        : "⚔️ Choisis tes skills !"}
                    </p>
                  </div>
                  <span className="duel-card-arrow">→</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Chercher un adversaire */}
        <div className="duel-section">
          <p className="duel-section-title">DÉFIER UN ONI</p>
          <div className="duel-search">
            <input
              type="text" value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Nom du joueur..."
            />
            <button className="btn btn-primary btn-sm"
              onClick={async () => {
                if (search.trim().length < 2) return;
                const { getDocs, limit: lim } = await import("firebase/firestore");
                const snap = await getDocs(query(collection(db, "users"), lim(30)));
                setAllUsers(snap.docs.map(d => ({uid:d.id,...d.data()}))
                  .filter(u => u.uid !== user.uid && u.demon &&
                    (u.displayName||"").toLowerCase().includes(search.toLowerCase())));
              }}>
              CHERCHER
            </button>
          </div>
          {allUsers.length > 0 && (
            <div className="duel-user-list">
              {allUsers.map(u => {
                const theirOni = enrichDemon(u.demon)?.oni;
                const existingDuel = activeDuels.find(d => d.participants.includes(u.uid));
                return (
                  <div key={u.uid} className="duel-user-row">
                    <OniAvatar oni={theirOni} size={44}/>
                    <div className="duel-user-info">
                      <p className="duel-user-name">{u.displayName}</p>
                      <p className="duel-user-oni">{theirOni?.name || "—"}</p>
                    </div>
                    {existingDuel ? (
                      <span className="badge badge-ghost">DUEL EN COURS</span>
                    ) : (
                      <button className="btn btn-primary btn-sm"
                        onClick={() => challengeUser(u)} disabled={challenging}>
                        ⚔️ DÉFIER
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Historique */}
        {completedDuels.length > 0 && (
          <div className="duel-section">
            <p className="duel-section-title">HISTORIQUE</p>
            {completedDuels.slice(0,5).map(duel => {
              const isChallenger = duel.challengerUid === user.uid;
              const iWon = duel.winnerUid === user.uid;
              const opponentName = isChallenger ? duel.opponentName : duel.challengerName;
              const opponentDemon = enrichDemon(isChallenger ? duel.opponentDemon : duel.challengerDemon);
              return (
                <div key={duel.id} className={`duel-history-row ${iWon ? "won" : "lost"}`}
                  onClick={() => setActiveDuel(duel)}>
                  <OniAvatar oni={opponentDemon?.oni} size={36}/>
                  <div className="duel-history-info">
                    <p>vs {opponentName}</p>
                    <p className="duel-history-sub">
                      {duel.result?.p1HP ?? "?"} — {duel.result?.p2HP ?? "?"}
                    </p>
                  </div>
                  <span className={`duel-history-badge ${iWon ? "won" : duel.winnerUid ? "lost" : "draw"}`}>
                    {iWon ? "VICTOIRE" : duel.winnerUid ? "DÉFAITE" : "ÉGALITÉ"}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Active duel modal */}
        {activeDuel && (
          <div className="modal-overlay" onClick={() => setActiveDuel(null)}>
            <div className="duel-modal" onClick={e => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setActiveDuel(null)}>✕</button>
              {activeDuel.status === "completed" ? (
                <DuelResultView duel={{
                  ...activeDuel,
                  result: activeDuel.result ? {
                    ...activeDuel.result,
                    rounds: (activeDuel.result.rounds || []).map(r => ({
                      ...r,
                      p1Skill: { id:r.p1SkillId, name:r.p1SkillName, icon:r.p1SkillIcon },
                      p2Skill: { id:r.p2SkillId, name:r.p2SkillName, icon:r.p2SkillIcon },
                    }))
                  } : null
                }} myUid={user.uid}/>
              ) : (
                <ActiveDuelView duel={activeDuel} myUid={user.uid} profile={profile}/>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
